import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ClockIcon, CheckCircleIcon, EditIcon, TrashIcon, BellIcon, CalendarIcon, PillIcon, ChevronDownIcon, ChevronUpIcon, HistoryIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { AddReminderModal } from '../components/modals/AddReminderModal';
import { MedicineReminderCard } from '../components/shared/MedicineReminderCard';
interface Reminder {
  id: string;
  medicineName: string;
  medicineStrength: string;
  medicineForm: string;
  dosage: string;
  frequency: string;
  timesPerDay: number;
  doseTimes: string[];
  daysOfWeek: number[];
  mealTiming: 'before' | 'after' | 'with';
  pillsPerDose: number;
  dosesPerIntake: number;
  currentStock: number;
  autoReorder: boolean;
  nextDose: Date;
  duration: number;
  reminderType: string[];
  notes?: string;
  status: 'active' | 'completed';
  createdAt: Date;
  snoozedUntil?: Date;
}
export function MedicineReminderPage() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  // Load reminders from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('medicineReminders');
    if (stored) {
      const parsed = JSON.parse(stored);
      setReminders(parsed.map((r: any) => ({
        ...r,
        nextDose: new Date(r.nextDose),
        createdAt: new Date(r.createdAt)
      })));
    } else {
      // Sample data
      setReminders([{
        id: '1',
        medicineName: 'Panadol Extra',
        medicineStrength: '500mg',
        medicineForm: 'Tablet',
        dosage: '1 tablet',
        frequency: 'Every 8 hours',
        timesPerDay: 3,
        doseTimes: ['08:00', '16:00', '00:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'after',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 20,
        autoReorder: true,
        nextDose: new Date(Date.now() + 2 * 60 * 60 * 1000),
        duration: 7,
        reminderType: ['push', 'sms'],
        notes: 'Take after meals',
        status: 'active',
        createdAt: new Date()
      }, {
        id: '2',
        medicineName: 'Vitamin D3',
        medicineStrength: '1000 IU',
        medicineForm: 'Capsule',
        dosage: '1 capsule',
        frequency: 'Once daily',
        timesPerDay: 1,
        doseTimes: ['09:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'with',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 30,
        autoReorder: false,
        nextDose: new Date(Date.now() + 5 * 60 * 60 * 1000),
        duration: 30,
        reminderType: ['push'],
        status: 'active',
        createdAt: new Date()
      }]);
    }
  }, []);
  // Save reminders to localStorage
  useEffect(() => {
    if (reminders.length > 0) {
      localStorage.setItem('medicineReminders', JSON.stringify(reminders));
    }
  }, [reminders]);
  const activeReminders = reminders.filter(r => r.status === 'active');
  const completedReminders = reminders.filter(r => r.status === 'completed');
  const handleAddReminder = (reminder: Omit<Reminder, 'id' | 'createdAt'>) => {
    const newReminder: Reminder = {
      ...reminder,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setReminders([...reminders, newReminder]);
    setShowAddModal(false);
  };
  const handleEditReminder = (id: string, updated: Omit<Reminder, 'id' | 'createdAt'>) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...updated,
      id: r.id,
      createdAt: r.createdAt
    } : r));
    setEditingReminder(null);
    setShowAddModal(false);
  };
  const handleMarkTaken = (id: string) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      status: 'completed' as const
    } : r));
  };
  const handleDeleteReminder = (id: string) => {
    setReminders(reminders.filter(r => r.id !== id));
  };
  const handleSnooze = (id: string) => {
    const snoozeUntil = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes from now
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      snoozedUntil: snoozeUntil,
      nextDose: snoozeUntil
    } : r));
  };
  const getNextDoseText = (nextDose: Date) => {
    const now = new Date();
    const diff = nextDose.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    if (diff < 0) return 'Overdue';
    if (hours === 0) return `In ${minutes} min`;
    if (hours < 24) return `In ${hours}h ${minutes}m`;
    return `In ${Math.floor(hours / 24)} days`;
  };
  const sortedActiveReminders = [...activeReminders].sort((a, b) => a.nextDose.getTime() - b.nextDose.getTime());
  const nextReminder = sortedActiveReminders[0];
  return <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7018" data-mp="1r2j8l6jomxni">
      {/* Desktop Header */}
      <div className="hidden md:block" data-id="element-7019" data-mp="1m13kt6q4ed1f">
        <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7020" data-mp="1u2kqtze8hoae" />
      </div>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7021" data-mp="230mwis8wx540">
        <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7022" data-mp="280hrb6vt6euc" />
      </div>
      <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7023" data-mp="1fdwye1zopuoo">
        {/* Page Hero Breadcrumb */}
        <PageHeroBreadcrumb breadcrumbs={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Medicine Reminders'
      }]} title="Medicine Reminders" description="Never miss a dose. Set up personalized reminders for your medications and track your medication schedule." data-id="element-7024" data-mp="1kaezmrvt3aw9" />
        <div className="container mx-auto px-4 py-8" data-id="element-7025" data-mp="p0s75xmp0nor">
          {/* Next Dose Alert */}
          {nextReminder && <div className="mb-6 bg-gradient-to-r from-[#1e2b55] to-[#162041] rounded-xl p-4 text-white shadow-lg" data-id="element-7026" data-mp="f4ow229v5epo">
              <div className="flex items-start justify-between" data-id="element-7027" data-mp="a6nbfcwsh7c3">
                <div className="flex-1" data-id="element-7028" data-mp="64a7mu1lrjhy">
                  <div className="flex items-center gap-2 mb-2" data-id="element-7029" data-mp="65burwvw7wg1">
                    <BellIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7030" data-mp="1dtulb1p1zw4" />
                    <span className="text-sm font-semibold" data-id="element-7031" data-mp="1c8wjbg67yti">Next Dose</span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-7032" data-mp="3sk4swzfbkmjr">
                    {nextReminder.medicineName}
                  </h3>
                  <p className="text-sm text-blue-100 mb-3" data-id="element-7033" data-mp="3ilns7aqkwtki">
                    {nextReminder.dosage} •{' '}
                    {getNextDoseText(nextReminder.nextDose)}
                  </p>
                  <div className="flex gap-2" data-id="element-7034" data-mp="16be0ana4qq2f">
                    <button onClick={() => handleMarkTaken(nextReminder.id)} className="bg-[#119c4b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f8a42] transition-colors" data-id="element-7035" data-mp="16cp4upbeg1ul">
                      ✅ Mark as Taken
                    </button>
                    <button onClick={() => handleSnooze(nextReminder.id)} className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-30 transition-colors" data-id="element-7036" data-mp="11agaboivorqd">
                      ⏰ Snooze 10 min
                    </button>
                  </div>
                </div>
                <ClockIcon className="w-12 h-12 text-blue-200 opacity-50" data-id="element-7037" data-mp="iml2el9y6856" />
              </div>
            </div>}
          {/* Active Reminders */}
          <div className="mb-8" data-id="element-7038" data-mp="8ndpfz74kfil">
            <div className="flex items-center justify-between mb-4" data-id="element-7039" data-mp="8mkgqsolvfxt">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-7040" data-mp="3m4fwp6aiogu5">
                Active Reminders ({activeReminders.length})
              </h2>
              <button onClick={() => setShowAddModal(true)} className="hidden md:flex items-center gap-2 bg-[#1e2b55] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7041" data-mp="3h778anyrzgkg">
                <PlusIcon className="w-4 h-4" data-id="element-7042" data-mp="8xxkt11dxo8o" />
                Add Reminder
              </button>
            </div>
            {activeReminders.length === 0 ? <div className="bg-white rounded-xl p-8 text-center shadow-sm" data-id="element-7043" data-mp="3yugxjx55o2a">
                <PillIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-7044" data-mp="hp7p9ndy34s5" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-7045" data-mp="donrchljrx4y">
                  No Active Reminders
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-7046" data-mp="rp6ylmi6uz62">
                  Start by adding your first medication reminder
                </p>
                <button onClick={() => setShowAddModal(true)} className="bg-[#1e2b55] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7047" data-mp="1d846mebyidkv">
                  Add Your First Reminder
                </button>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7048" data-mp="zhil0hv4bpja">
                {sortedActiveReminders.map(reminder => <MedicineReminderCard key={reminder.id} reminder={reminder} onMarkTaken={handleMarkTaken} onEdit={r => {
              setEditingReminder(r);
              setShowAddModal(true);
            }} onDelete={handleDeleteReminder} onSnooze={handleSnooze} data-id="element-7049" data-mp="1w4vxb8ttvuyy" />)}
              </div>}
          </div>
          {/* Smart Suggestions */}
          {completedReminders.length > 0 && <div className="mb-8" data-id="element-7050" data-mp="3sv2htgy531zf">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-7051" data-mp="1n7u9cgvqtxv">
                💡 Smart Suggestions
              </h2>
              <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-7052" data-mp="6lrgldzdy7qr">
                <p className="text-sm text-gray-600 mb-3" data-id="element-7053" data-mp="6kgerjpi3a2a">
                  Based on your history
                </p>
                <div className="space-y-2" data-id="element-7054" data-mp="fev2mao3jsx0">
                  {completedReminders.slice(0, 2).map(reminder => <button key={reminder.id} onClick={() => {
                setEditingReminder({
                  ...reminder,
                  id: Date.now().toString(),
                  status: 'active',
                  nextDose: new Date()
                });
                setShowAddModal(true);
              }} className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-7055" data-mp="kcwmgxtcjw9j">
                      <div className="flex items-center gap-3" data-id="element-7056" data-mp="2j3zfvrkc1vlc">
                        <PillIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7057" data-mp="2j5u3xeqwi3tn" />
                        <div className="text-left" data-id="element-7058" data-mp="2y2bdq1jfzae3">
                          <p className="text-sm font-medium text-gray-900" data-id="element-7059" data-mp="2t1x8er986ejr">
                            {reminder.medicineName}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-7060" data-mp="36zlt9z3allot">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                        </div>
                      </div>
                      <PlusIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7061" data-mp="3gt2jyu84uko9" />
                    </button>)}
                </div>
              </div>
            </div>}
          {/* History Section */}
          <div className="mb-8" data-id="element-7062" data-mp="3lqkweulczi10">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-7063" data-mp="3lrm72xygq2i7">
              <div className="flex items-center gap-3" data-id="element-7064" data-mp="d67rccx0z6fb">
                <HistoryIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7065" data-mp="3vcvjl26z7z3k" />
                <h2 className="text-xl font-bold text-gray-900" data-id="element-7066" data-mp="47lzts9ksh2e">
                  📜 History ({completedReminders.length})
                </h2>
              </div>
              {showHistory ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-7067" data-mp="3qeu2gmt429uq" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-7068" data-mp="3qgooy5k16a0x" />}
            </button>
            {showHistory && <div className="mt-4 space-y-3" data-id="element-7069" data-mp="3go95t4wzzfeg">
                {completedReminders.length === 0 ? <div className="bg-white rounded-xl p-6 text-center" data-id="element-7070" data-mp="32ozr69ofwtcl">
                    <p className="text-gray-500" data-id="element-7071" data-mp="37ocknd9tgyo5">No completed reminders yet</p>
                  </div> : completedReminders.map(reminder => <div key={reminder.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-gray-300" data-id="element-7072" data-mp="dy7a5sohng2q">
                      <div className="flex items-start justify-between" data-id="element-7073" data-mp="5283uagur4qi">
                        <div className="flex-1" data-id="element-7074" data-mp="46itjzv8vce">
                          <div className="flex items-center gap-2 mb-1" data-id="element-7075" data-mp="3rb99mabskzm3">
                            <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-7076" data-mp="3rckeyekvyffb" />
                            <h3 className="font-semibold text-gray-600" data-id="element-7077" data-mp="3mbefzz0mtvxc">
                              {reminder.medicineName}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500" data-id="element-7078" data-mp="3dct4gntkvh62">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-7079" data-mp="1bkeed1gvr6ek">
                            Completed on{' '}
                            {reminder.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteReminder(reminder.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-7080" data-mp="1o15ymqmyrue9">
                          <TrashIcon className="w-4 h-4" data-id="element-7081" data-mp="1xumqhp3jizfa" />
                        </button>
                      </div>
                    </div>)}
              </div>}
          </div>
        </div>
      </main>
      {/* Floating Action Button (Mobile) */}
      <button onClick={() => setShowAddModal(true)} className="md:hidden fixed bottom-20 right-4 w-14 h-14 bg-[#119c4b] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#0f8a42] transition-colors z-40" data-id="element-7082" data-mp="1a527x7bau46j">
        <PlusIcon className="w-6 h-6" data-id="element-7083" data-mp="2piya2p3dbwgf" />
      </button>
      {/* Add/Edit Reminder Modal */}
      <AddReminderModal isOpen={showAddModal} onClose={() => {
      setShowAddModal(false);
      setEditingReminder(null);
    }} onSave={editingReminder ? reminder => handleEditReminder(editingReminder.id, reminder) : handleAddReminder} initialData={editingReminder} data-id="element-7084" data-mp="qe6oqqko73qi" />
      <Footer data-id="element-7085" data-mp="ueqgtfqdzh5s" />
      <MobileFooterNav data-id="element-7086" data-mp="6qpewpnpqyzc" />
    </div>;
}